import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@travellegacy/nexops-design-system';
import { MonthYearSelector } from '@travellegacy/nexops-design-system';
import {
  SortableTable,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@travellegacy/nexops-design-system';
import { commissionsAPI } from '@/shared/lib/api';
import { formatCurrency } from '@/shared/lib/utils';
import { Loader2, Calendar, DollarSign } from 'lucide-react';
import type { CommissionSummary, ServiceType } from '@/shared/types';
import { serviceTypeLabels } from '@/shared/types';

export default function CommissionsPage() {
  // Estado para mês/ano selecionado (padrão: mês atual)
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1); // 1-12
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());

  // Query para buscar comissões de todos os colaboradores
  const { data: summariesResponse, isLoading } = useQuery({
    queryKey: ['all-employees-commissions', selectedMonth, selectedYear],
    queryFn: async () => {
      const response = await commissionsAPI.getAllEmployeesSummary(selectedMonth, selectedYear);
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // Cache por 5 minutos
  });

  const summaries = (summariesResponse?.data || []) as CommissionSummary[];

  // Calcular total geral
  const totalGeneral = summaries.reduce((sum, s) => sum + s.totalCommissions, 0);
  const totalRecordsGeneral = summaries.reduce((sum, s) => sum + s.totalRecords, 0);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold">Comissões</h1>
          <p className="text-muted-foreground">
            Resumo de comissões de todos os colaboradores
          </p>
        </div>

        <MonthYearSelector
          selectedMonth={selectedMonth}
          selectedYear={selectedYear}
          onMonthChange={setSelectedMonth}
          onYearChange={setSelectedYear}
          disableFutureMonths={true}
        />
      </div>

        {/* Cards de Resumo Geral */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Total de Comissões
                  </p>
                  <p className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">
                    {formatCurrency(totalGeneral)}
                  </p>
                </div>
                <div className="p-3 bg-green-100 dark:bg-green-900/20 rounded-lg">
                  <DollarSign className="h-6 w-6 text-green-600 dark:text-green-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Total de Emissões
                  </p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                    {totalRecordsGeneral}
                  </p>
                </div>
                <div className="p-3 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                  <Calendar className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabela de Comissões por Colaborador */}
        <Card>
          <CardHeader>
            <CardTitle>Comissões por Colaborador</CardTitle>
          </CardHeader>
          <CardContent>
            {summaries.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                Nenhuma comissão registrada neste período
              </p>
            ) : (
              <SortableTable
                data={summaries}
                defaultSortKey="totalCommissions"
                defaultSortDirection="desc"
              >
                {(sortedSummaries) => (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead sortable sortKey="employeeName">Colaborador</TableHead>
                        <TableHead sortable sortKey="totalCommissions" align="right">Total</TableHead>
                        <TableHead sortable sortKey="totalRecords" align="center">Emissões</TableHead>
                        <TableHead align="center">Por Tipo</TableHead>
                        <TableHead sortable sortKey="level1Amount" align="right">Nível 01</TableHead>
                        <TableHead sortable sortKey="level2Amount" align="right">Nível 02</TableHead>
                        <TableHead sortable sortKey="level3Amount" align="right">Nível 03</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortedSummaries.map((summary) => {
                        return (
                          <TableRow key={summary.employeeId}>
                            <TableCell>
                              <span className="font-medium text-gray-900 dark:text-white">
                                {summary.employeeName}
                              </span>
                            </TableCell>
                            <TableCell>
                              <div className="text-right font-semibold text-green-600 dark:text-green-400">
                                {formatCurrency(summary.totalCommissions)}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="text-center">
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400">
                                  {summary.totalRecords}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-1 justify-center">
                                {summary.byServiceType.map((st) => (
                                  <div
                                    key={st.serviceType}
                                    className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300"
                                    title={`${serviceTypeLabels[st.serviceType as ServiceType]}: ${formatCurrency(st.total)}`}
                                  >
                                    {serviceTypeLabels[st.serviceType as ServiceType]}: {st.count}
                                  </div>
                                ))}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="text-right">
                                <div className="font-semibold text-green-600 dark:text-green-400">
                                  {formatCurrency(summary.level1Amount)}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="text-right">
                                <div className="font-semibold text-blue-600 dark:text-blue-400">
                                  {formatCurrency(summary.level2Amount)}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="text-right">
                                <div className="font-semibold text-purple-600 dark:text-purple-400">
                                  {formatCurrency(summary.level3Amount)}
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </SortableTable>
            )}
          </CardContent>
        </Card>
    </div>
  );
}
